import type { Highlight, Media, Message } from '../../api/types'
import { groesse } from '../../lib/format'
import { datumText, zeitText } from '../../lib/ortszeit'

/**
 * Ein Medium im Buch.
 *
 * **Ein Video wird zu seinem Standbild** — das liegt seit dem Hochladen auf
 * dem Server (`media.poster_name`, ausgeliefert über `/poster`), es muss also
 * nichts gerechnet werden. Was ein Buch aus einem Film sonst machen könnte,
 * gibt es nicht: bewegte Bilder auf Papier sind keine.
 *
 * **Und kein Verweis in die App.** Er wäre in einem verschenkten Buch tot und
 * verlangte obendrein eine Anmeldung. Stattdessen steht daneben, was es ist —
 * Dauer, Auflösung, Kodierung —, damit man das Video in der App wiederfindet.
 *
 * Sprachnachrichten haben kein Bild und werden zu einer Zeile.
 */
export function Medium({ media }: { media: Media }) {
  if (media.kind === 'audio') {
    return (
      <p className="buch__ton">
        Sprachnachricht · {Math.round(media.duration_ms / 1000)} s · {groesse(media.bytes)}
      </p>
    )
  }

  if (media.kind === 'video') {
    const dauer = Math.round(media.duration_ms / 1000)
    const minuten = Math.floor(dauer / 60)
    const sekunden = String(dauer % 60).padStart(2, '0')
    return (
      <figure className="buch__bild buch__bild--video">
        {media.poster_url !== null ? (
          <img src={media.poster_url} alt="" loading="eager" />
        ) : (
          <div className="buch__kein-standbild">Ohne Standbild</div>
        )}
        <figcaption>
          Video · {minuten}:{sekunden}
          {media.height > 0 && ` · ${media.height}p`}
          {media.codec !== undefined && media.codec !== '' && ` · ${media.codec}`}
        </figcaption>
      </figure>
    )
  }

  return (
    <figure className="buch__bild">
      <img src={media.url} alt="" loading="eager" />
    </figure>
  )
}

/**
 * Ein Beitrag im Buch.
 *
 * `break-inside: avoid` steht im Stylesheet: ein Beitrag soll nicht mitten im
 * Satz auf die nächste Seite rutschen. Bei einem sehr langen Text gewinnt der
 * Browser — das ist richtig so, sonst bliebe eine halbe Seite leer.
 */
export function Beitrag({ nachricht, versatz }: { nachricht: Message; versatz: number | null }) {
  if (nachricht.deleted) return null

  return (
    <article className="buch__beitrag">
      <p className="buch__zeit">{zeitText(nachricht.created_at, versatz)}</p>
      {nachricht.body !== '' && <p className="buch__text">{nachricht.body}</p>}
      {nachricht.media.map((m) => (
        <Medium key={m.id} media={m} />
      ))}
      {nachricht.reactions.length > 0 && (
        <p className="buch__reaktionen">
          {nachricht.reactions.map((r) => r.emoji).join(' ')}
        </p>
      )}
    </article>
  )
}

/**
 * Ein Highlight als Abschnitt.
 *
 * Die Zeit steht in der **Uhr der Reise** und nicht in der des Geräts, das
 * gerade druckt — sonst läse man jede Ankunft um Stunden verschoben. Dieselbe
 * Regel wie überall sonst (`tz_offset_min`), und der Punkt bringt seine eigene
 * mit, falls er eine hat.
 *
 * **Die Nennung des Wetterdienstes ist Pflicht**, nicht Zierde: WeatherKit
 * verlangt sie sichtbar dort, wo das Wetter steht.
 */
export function Punktseite({
  punkt,
  nummer,
  reiseVersatz,
}: {
  punkt: Highlight
  nummer: number
  reiseVersatz: number | null
}) {
  const versatz = punkt.tz_offset_min ?? reiseVersatz
  const wetter = punkt.weather as { temp_c?: number; text?: string } | null

  return (
    <section className="buch__punkt">
      <h3 className="buch__punkt-titel">
        <span className="buch__nummer">{nummer}</span>
        {punkt.title}
      </h3>
      <p className="buch__punkt-zeile">
        {punkt.place_name !== '' && <span>{punkt.place_name}</span>}
        {punkt.happened_at !== null && <span>{datumText(punkt.happened_at, versatz)}</span>}
        {wetter?.temp_c !== undefined && (
          <span>
            {Math.round(wetter.temp_c)} °C{wetter.text !== undefined && `, ${wetter.text}`}
            <span className="buch__quelle"> (Apple Weather)</span>
          </span>
        )}
      </p>
      {punkt.body !== '' && <p className="buch__text">{punkt.body}</p>}
      {punkt.media.map((m) => (
        <Medium key={m.id} media={m} />
      ))}
    </section>
  )
}
